import React, { useState } from 'react';
import { Bell, BellRing, CheckCheck, X } from 'lucide-react';
import { useNotifications, Notification, UseNotificationsResult } from './useNotifications';
import { getNotificationVisual } from './notificationVisuals';
import { requestNotificationPermission, canRequestNotificationPermission } from '@clareefai/utils';

export interface NotificationBellProps {
  /** Firestore collection path: "vendors" | "promoters" | "prospects" | "admins" */
  collectionPath: string;
  /** Firebase UID of the current user */
  firebaseUid: string | null;
  /** App name for document title */
  appName: string;
  /** Visual variant */
  variant?: 'default' | 'light' | 'dark';
  /** Called when user clicks a notification — use to navigate based on type */
  onNotificationClick?: (notification: Notification) => void;
  /** Optional custom render for each notification row */
  renderNotification?: (notification: Notification, defaultContent: React.ReactNode) => React.ReactNode;
  /** Called when new notifications arrive (not fired for historical or read-updates) */
  onNewNotifications?: (newNotifications: Notification[]) => void;
  /**
   * When the host already owns a `useNotifications` subscription, pass its
   * result here to avoid opening a second Firestore listener (and firing
   * sounds/browser notifications twice).
   */
  source?: UseNotificationsResult;
}

// Self-contained keyframes so the panel + rows animate consistently across every
// consuming app, regardless of each app's Tailwind config. Reduced-motion is honored.
const PANEL_ANIMATION_CSS = `
@keyframes clfNotifPop {
  from { opacity: 0; transform: translateY(-6px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
}
@keyframes clfNotifRow {
  from { opacity: 0; transform: translateY(8px); filter: blur(4px); }
  to   { opacity: 1; transform: translateY(0); filter: blur(0); }
}
@media (prefers-reduced-motion: reduce) {
  [data-clf-notif] { animation: none !important; }
}
`;

// Layered shadow stands in for a hard border — a hairline ring plus soft ambient depth
// so the floating panel reads as elevated on any background.
const PANEL_SHADOW =
  '0 0 0 1px rgba(0,0,0,0.06), 0 12px 28px -8px rgba(0,0,0,0.18), 0 6px 12px -6px rgba(0,0,0,0.08)';

function formatTime(timestamp: number): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  if (diffInSeconds < 45) return 'Just now';
  if (diffInSeconds < 3600) return `${Math.max(1, Math.floor(diffInSeconds / 60))}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 172800) return 'Yesterday';
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export const NotificationBell: React.FC<NotificationBellProps> = ({
  collectionPath,
  firebaseUid,
  appName,
  variant = 'default',
  onNotificationClick,
  renderNotification,
  onNewNotifications,
  source,
}) => {
  const internal = useNotifications({
    collectionPath,
    firebaseUid,
    appName,
    onNewNotifications,
    enabled: !source,
  });
  const { notifications, unreadCount, markAsRead, markAllAsRead } = source ?? internal;
  const [isOpen, setIsOpen] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission | null>(null);

  const handleBellClick = () => {
    const next = !isOpen;
    setIsOpen(next);
    if (next) {
      if (typeof window !== 'undefined' && 'Notification' in window) setPermission(window.Notification.permission);
      if (canRequestNotificationPermission()) requestNotificationPermission().then(setPermission);
    }
  };

  const currentPermission = permission ?? (typeof window !== 'undefined' && 'Notification' in window ? window.Notification.permission : 'denied');
  const showEnableBanner = currentPermission === 'default';

  const pressable = 'transition-[background-color,color,transform] duration-200 ease-out active:scale-[0.96]';
  const buttonClass =
    variant === 'dark'
      ? `relative p-2 rounded-full text-white hover:text-gray-200 hover:bg-indigo-700 ${pressable}`
      : variant === 'light'
        ? `relative p-2 rounded-lg hover:bg-white/10 ${pressable}`
        : `relative p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 ${pressable}`;
  const iconClass = variant === 'light' ? 'h-6 w-6 text-white' : 'h-6 w-6';

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.read) markAsRead(notification.id);
    onNotificationClick?.(notification);
    setIsOpen(false);
  };

  const defaultNotificationContent = (notification: Notification) => (
    <>
      <p className={`text-sm leading-snug text-pretty ${notification.read ? 'font-medium text-gray-700' : 'font-semibold text-gray-900'}`}>
        {notification.title}
      </p>
      <p className="mt-0.5 text-[13px] leading-snug text-gray-500 text-pretty line-clamp-2">{notification.message}</p>
      <p className="mt-1 text-xs text-gray-400 tabular-nums">{formatTime(notification.createdAt)}</p>
    </>
  );

  if (!firebaseUid) return null;

  return (
    <div className="relative">
      <style>{PANEL_ANIMATION_CSS}</style>

      <button onClick={handleBellClick} className={buttonClass} aria-label="Notifications">
        <Bell className={iconClass} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold leading-none text-white ring-2 ring-white tabular-nums">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
          <div
            data-clf-notif
            className="absolute right-0 mt-3 flex max-h-[600px] w-[22rem] flex-col overflow-hidden rounded-2xl bg-white sm:w-96 z-20"
            style={{ transformOrigin: 'top right', animation: 'clfNotifPop 220ms cubic-bezier(0.2,0,0,1)', boxShadow: PANEL_SHADOW }}
          >
            <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100">
              <h3 className="flex items-center gap-2 text-[15px] font-semibold text-gray-900">
                Notifications
                {unreadCount > 0 && (
                  <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-indigo-600 px-1.5 text-xs font-semibold text-white tabular-nums">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </h3>
              <div className="ml-auto flex items-center gap-1">
                {unreadCount > 0 && (
                  <button
                    onClick={markAllAsRead}
                    className={`inline-flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-xs font-medium text-indigo-600 hover:bg-indigo-50 ${pressable}`}
                  >
                    <CheckCheck className="h-3.5 w-3.5" />
                    Mark all read
                  </button>
                )}
                <button
                  onClick={() => setIsOpen(false)}
                  aria-label="Close notifications"
                  className={`inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 ${pressable}`}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {showEnableBanner && (
              <button
                onClick={() => requestNotificationPermission().then((p) => setPermission(p))}
                className="flex w-full items-center gap-2.5 border-b border-indigo-100 bg-indigo-50/70 px-4 py-2.5 text-left transition-colors hover:bg-indigo-50"
              >
                <BellRing className="h-4 w-4 shrink-0 text-indigo-600" />
                <span className="text-xs text-indigo-900 text-pretty">
                  <span className="font-medium">Enable browser notifications</span> to get alerts when this tab is in the background
                </span>
              </button>
            )}

            <div className="flex-1 overflow-y-auto overscroll-contain">
              {notifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center px-8 py-14 text-center">
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gray-50 text-gray-300">
                    <Bell className="h-7 w-7" />
                  </div>
                  <p className="mt-3 text-sm font-medium text-gray-700">You're all caught up</p>
                  <p className="mt-1 text-xs text-gray-400 text-pretty">New notifications will show up here.</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {notifications.map((notification, index) => {
                    const { Icon, tile } = getNotificationVisual(notification.type);
                    const content = renderNotification
                      ? renderNotification(notification, defaultNotificationContent(notification))
                      : defaultNotificationContent(notification);
                    return (
                      <div
                        key={notification.id}
                        data-clf-notif
                        role="button"
                        tabIndex={0}
                        onClick={() => handleNotificationClick(notification)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleNotificationClick(notification);
                          }
                        }}
                        style={{ animation: 'clfNotifRow 360ms cubic-bezier(0.2,0,0,1) backwards', animationDelay: `${Math.min(index, 8) * 45}ms` }}
                        className={`flex w-full cursor-pointer items-start gap-3 px-4 py-3 text-left outline-none transition-colors focus-visible:bg-gray-50 ${
                          notification.read ? 'bg-white hover:bg-gray-50' : 'bg-indigo-50/40 hover:bg-indigo-50/70'
                        }`}
                      >
                        <span className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${tile}`}>
                          <Icon className="h-[18px] w-[18px]" strokeWidth={2} />
                        </span>
                        <div className="min-w-0 flex-1">{content}</div>
                        {!notification.read && (
                          <span className="relative mt-1.5 flex h-2.5 w-2.5 shrink-0">
                            <span className="absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-60 animate-ping motion-reduce:hidden" />
                            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-indigo-500" />
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
